<template>
  <Teleport to="body">
    <div class="download-progress-container" v-if="downloads.length > 0">
      <div class="download-warning" v-if="hasActiveDownloads">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>Leaving the site will cancel active downloads</span>
      </div>
      <div class="download-warning safari-warning" v-if="isSafari">
        <font-awesome-icon :icon="['fas', 'info-circle']" />
        <span>Safari may have issues with downloads. For best results, use Chrome, Edge or Firefox.</span>
      </div>
      <div class="download-progress-items">
        <TransitionGroup name="download-list">
          <div class="download-item" 
              v-for="download in downloads" 
              :key="download.id"
              :class="{'failed': download.status === 'error'}">
            <div class="download-info">
              <div class="download-filename" :title="download.fileName">
                {{ download.fileName }}
              </div>
              <div class="download-status">
                <template v-if="download.status === 'downloading'">
                  {{ formatSize(download.loaded) }} / {{ formatSize(download.total) }}
                </template>
                <template v-else-if="download.status === 'completed'">
                  Completed
                </template>
                <template v-else-if="download.status === 'error'">
                  Failed
                </template>
                <template v-else-if="download.status === 'cancelled'">
                  Cancelled
                </template>
                <template v-else>
                  {{ download.status }}
                </template>
              </div>
            </div>
            <div class="download-progress-bar-container">
              <div 
                class="download-progress-bar" 
                :class="downloadStatusClass(download)"
                :style="{ width: download.status === 'error' ? '100%' : `${download.progress}%` }">
              </div>
            </div>
            <div class="download-actions">
              <button 
                v-if="download.status === 'downloading'"
                @click="cancelDownload(download.id)" 
                class="download-cancel-btn" 
                title="Cancel download">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
              <template v-else-if="download.status === 'error'">
                <button 
                  @click="retryDownload(download)" 
                  class="download-retry-btn"
                  title="Retry download">
                  <font-awesome-icon :icon="['fas', 'redo']" style="margin-right:4px" />Retry
                </button>
                <button 
                  @click="clearDownload(download.id)" 
                  class="download-clear-btn"
                  title="Clear from list">
                  <font-awesome-icon :icon="['fas', 'times']" />
                </button>
              </template>
              <button 
                v-else
                @click="clearDownload(download.id)" 
                class="download-clear-btn"
                title="Clear from list">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
            </div>
          </div>
        </TransitionGroup>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'DownloadProgress',
  setup() {
    const store = useStore();
    
    const downloads = computed(() => store.getters['downloads/allDownloads']);
    const hasActiveDownloads = computed(() => store.getters['downloads/hasActiveDownloads']);
    const isSafari = computed(() => /^((?!chrome|android).)*safari/i.test(navigator.userAgent));
    
    const cancelDownload = (id) => {
      store.dispatch('downloads/cancelDownload', id);
    };
    
    const clearDownload = (id) => {
      store.dispatch('downloads/clearDownload', id);
    };

    const retryDownload = (download) => {
      store.dispatch('downloads/downloadFile', {
        url: download.url,
        fileName: download.fileName
      });
      clearDownload(download.id);
    };
    
    const formatSize = (bytes) => {
      if (!bytes) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return `${size.toFixed(1)} ${units[i]}`;
    };
    
    const downloadStatusClass = (download) => {
      if (download.status === 'error') return 'download-progress-error';
      switch (download.status) {
        case 'downloading':
          return 'download-progress-downloading';
        case 'completed':
          return 'download-progress-completed';
        case 'cancelled':
          return 'download-progress-cancelled';
        default:
          return '';
      }
    };
    
    return {
      downloads,
      hasActiveDownloads,
      cancelDownload,
      clearDownload,
      retryDownload,
      formatSize,
      downloadStatusClass,
      isSafari
    };
  }
}
</script>

<style>
.download-progress-container {
  position: fixed;
  top: 15px;
  right: 20px;
  max-width: 458px;
  width: 100%;
  z-index: 9999;
  pointer-events: auto;
}

.download-warning {
  background-color: #f4f6dc;
  color: #63640c;
  padding: 8px 12px;
  border-radius: 8px 8px 0 0;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.download-warning svg {
  color: #ffa500;
}

.download-warning.safari-warning {
  background-color: #e8f4fd;
  color: #31708f;
  border-color: #bce8f1;
  border-radius: 0;
}

.download-warning.safari-warning svg {
  color: #31708f;
}

.download-progress-items {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
}

.download-item {
  padding: 12px 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
}

.download-item.failed .download-info {
    margin-right: 65px;
}

.download-item:last-child {
  border-bottom: none;
}

.download-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.download-filename {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 290px;
}

.download-status {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  margin-right: 20px;
}

.download-progress-bar-container {
  height: 4px;
  background-color: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 6px;
}

.download-progress-bar {
  height: 100%;
  transition: width 0.2s ease-in-out;
}

.download-progress-downloading {
  background-color: #c8cb18;
}

.download-progress-completed {
  background-color: #4caf50;
}

.download-progress-error {
  background-color: #f44336 !important;
}

.download-progress-cancelled {
  background-color: #9e9e9e;
}

.download-actions {
  position: absolute;
  top: 14px;
  right: 15px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
}

.download-cancel-btn, 
.download-clear-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  opacity: 0.6;
  padding: 2px;
  font-size: 14px;
  line-height: 1;
  transition: opacity 0.2s;
}

.download-cancel-btn:hover, 
.download-clear-btn:hover {
  opacity: 1;
}

.download-retry-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #f44336;
  opacity: 0.8;
  padding: 0 8px 2px 8px;
  font-size: 14px;
  line-height: 1;
  font-weight: bold;
  display: flex;
  align-items: center;
  transition: opacity 0.2s;
}

.download-retry-btn:hover {
  opacity: 1;
}

.download-list-enter-active,
.download-list-leave-active {
  transition: all 0.3s;
}

.download-list-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.download-list-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

@media screen and (max-width: 600px) {
  .download-progress-container {
    top: 140px;
    right: 10px;
    max-width: 90%;
    width: calc(100% - 20px);
    left: 50%;
    transform: translateX(-50%);
  }
  
  .download-filename {
    max-width: 200px;
  }
}
</style>